/**
 * SUPPORT-ONLY. Not exposed to clients.
 *
 * Turns a parsed receipt's billed line items into a `TimelineBand` lane
 * (charging / idle / free spans) that overlays on the OCPP timeline via the
 * Explorer's `extraBands` prop. Ported from the demo's demoUtils.ts.
 */
import type { BandSpan, TimelineBand } from '@chargelab/ocpp-log-visualizer';
import type { ReceiptLineItem, ReceiptSummary } from './receiptParser';

export function fmtDur(s?: number): string | undefined {
  if (s == null) return undefined;
  const t = Math.round(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h}h`);
  if (h || m) parts.push(`${m}m`);
  parts.push(`${sec}s`);
  return parts.join(' ');
}

export const fmtMoney = (v: number | undefined, currency?: string): string | undefined =>
  v == null
    ? undefined
    : new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(
        v,
      );

const rateUnit = (kind: string) =>
  kind === 'BILLED_BY_KWH' ? '/kWh' : kind === 'IDLE_RATE' ? '/min' : '';

export function fmtQtyAtRate(li: ReceiptLineItem, currency?: string): string | undefined {
  const qty =
    li.energyKwh != null
      ? `${li.energyKwh.toFixed(2)} kWh`
      : li.durationSeconds != null
      ? `period ${fmtDur(li.durationSeconds)}`
      : undefined;
  const rate =
    li.unitPrice != null ? `@ ${fmtMoney(li.unitPrice, currency)}${rateUnit(li.kind)}` : undefined;
  return [qty, rate].filter(Boolean).join(' ') || undefined;
}

const BILLING_STYLES: Record<string, { label: string; color: string }> = {
  BILLED_BY_KWH: { label: 'Charging', color: 'var(--ocpp-status-Charging, #22c55e)' },
  BILLED_BY_TIME: { label: 'Charging', color: 'var(--ocpp-status-Charging, #22c55e)' },
  IDLE_RATE: { label: 'Idle', color: 'rgba(217, 119, 6, 0.85)' },
  FREE: { label: 'Free', color: 'rgba(100, 116, 139, 0.55)' },
};
const UNKNOWN_BILLING_COLOR = 'rgba(124, 58, 237, 0.8)';

export function buildBillingBand(
  summary: ReceiptSummary,
  session?: { start: Date; end: Date },
): TimelineBand | null {
  const billed = summary.lineItems
    .filter(
      (li) => li.startTimeMs != null && li.stopTimeMs != null && li.stopTimeMs > li.startTimeMs,
    )
    .sort((a, b) => a.startTimeMs! - b.startTimeMs!);
  if (!billed.length) return null;

  const GAP_MS = 2_000;
  const freeSpan = (startMs: number, endMs: number): BandSpan => ({
    start: new Date(startMs),
    end: new Date(endMs),
    label: 'Free',
    color: BILLING_STYLES.FREE.color,
    title: `Free · ${fmtDur((endMs - startMs) / 1000)} — no billed line item for this period`,
  });

  const spans: BandSpan[] = [];
  let cursor = Math.min(session?.start.getTime() ?? Infinity, billed[0].startTimeMs!);
  for (const li of billed) {
    if (li.startTimeMs! - cursor > GAP_MS) spans.push(freeSpan(cursor, li.startTimeMs!));
    const style = BILLING_STYLES[li.kind] ?? { label: li.label, color: UNKNOWN_BILLING_COLOR };
    spans.push({
      start: new Date(li.startTimeMs!),
      end: new Date(li.stopTimeMs!),
      label: style.label,
      color: style.color,
      title: [style.label, fmtQtyAtRate(li, summary.currency), fmtMoney(li.total, summary.currency)]
        .filter(Boolean)
        .join(' · '),
    });
    cursor = Math.max(cursor, li.stopTimeMs!);
  }
  const sessionEnd = session?.end.getTime();
  if (sessionEnd != null && sessionEnd - cursor > GAP_MS) spans.push(freeSpan(cursor, sessionEnd));

  return { id: 'receipt-billing', gutterLabel: 'billing', spans };
}
